/**
 * useAuditLogs Hook
 *
 * Custom hook for fetching and managing audit log data with:
 * - Filtering by date range, user, and action type
 * - Pagination support
 * - User list for filter dropdown
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  getAuditLogs,
  getAuditUsers,
} from '../services/auditApi';
import {
  AuditLogEntry,
  AuditUser,
  AuditLogFilters,
  AuditAction,
} from '../types/audit';

interface UseAuditLogsParams {
  /** Initial filters */
  initialFilters?: Partial<AuditLogFilters>;
  /** Auto-load on mount */
  autoLoad?: boolean;
}

interface UseAuditLogsResult {
  /** Audit log entries */
  logs: AuditLogEntry[];
  /** Available users for filter dropdown */
  users: AuditUser[];
  /** Current filters */
  filters: AuditLogFilters;
  /** Loading state for logs */
  loading: boolean;
  /** Loading state for users */
  loadingUsers: boolean;
  /** Error message */
  error: string | null;
  /** Whether there are more logs to load */
  hasMore: boolean;
  /** Total loaded count */
  totalLoaded: number;
  /** Update filters (resets pagination) */
  setFilters: (filters: Partial<AuditLogFilters>) => void;
  /** Load more logs (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh logs with current filters */
  refresh: () => Promise<void>;
  /** Reload users list */
  refreshUsers: () => Promise<void>;
}

/**
 * Get default date range (last 30 days)
 */
function getDefaultDateRange(): { startDate: string; endDate: string } {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

export function useAuditLogs({
  initialFilters,
  autoLoad = true,
}: UseAuditLogsParams = {}): UseAuditLogsResult {
  // Initialize filters with defaults
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [filters, setFiltersState] = useState<AuditLogFilters>({
    startDate: initialFilters?.startDate || defaultRange.startDate,
    endDate: initialFilters?.endDate || defaultRange.endDate,
    userId: initialFilters?.userId,
    action: initialFilters?.action,
  });

  // Logs state
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<AuditUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Derived values
  const hasMore = nextToken !== undefined;
  const totalLoaded = logs.length;

  // Load audit logs
  const loadLogs = useCallback(async (resetPagination: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const token = resetPagination ? undefined : nextToken;
      const response = await getAuditLogs(filters, token);

      if (response.success && response.items) {
        if (resetPagination) {
          setLogs(response.items);
        } else {
          setLogs(prev => [...prev, ...response.items!]);
        }
        setNextToken(response.nextToken);
      } else {
        setError(response.error || 'Failed to load audit logs');
        if (resetPagination) {
          setLogs([]);
        }
      }
    } catch (err: any) {
      console.error('Error loading audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
      if (resetPagination) {
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, nextToken]);

  // Load users for filter dropdown
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);

    try {
      const response = await getAuditUsers();

      if (response.success && response.users) {
        setUsers(response.users);
      } else {
        console.error('Failed to load audit users:', response.error);
      }
    } catch (err: any) {
      console.error('Error loading audit users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Update filters (resets pagination)
  const setFilters = useCallback((newFilters: Partial<AuditLogFilters>) => {
    setFiltersState(prev => ({
      ...prev,
      ...newFilters,
    }));
    // Reset pagination when filters change
    setNextToken(undefined);
    setLogs([]);
  }, []);

  // Load more (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await loadLogs(false);
  }, [hasMore, loading, loadLogs]);

  // Refresh with current filters
  const refresh = useCallback(async () => {
    setNextToken(undefined);
    await loadLogs(true);
  }, [loadLogs]);

  // Refresh users
  const refreshUsers = useCallback(async () => {
    await loadUsers();
  }, [loadUsers]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      loadLogs(true);
      loadUsers();
    }
  }, []); // Only run once on mount

  // Reload when filters change
  useEffect(() => {
    // Skip initial load (handled above)
    if (logs.length === 0 && !loading && !error) return;
    loadLogs(true);
  }, [filters.startDate, filters.endDate, filters.userId, filters.action]);

  return {
    logs,
    users,
    filters,
    loading,
    loadingUsers,
    error,
    hasMore,
    totalLoaded,
    setFilters,
    loadMore,
    refresh,
    refreshUsers,
  };
}
